// BASE DO PRODUTO — conteudo escrito uma vez, valido para todas as cores e
// todos os canais (W5, plano secao 5 W5 + README "Ficha de produto").
// Reune: Sisplan read-only, campos com override por canal, todas as fotos do produto,
// tamanhos no canal, tecnicidades e SEO/preco. Substitui a antiga
// "Aba Visão geral" + "Aba SEO" + "Aba Promoção" (Check 0 secao 6.3 — cada
// mutation abaixo e a MESMA de antes do redesign, so a moldura visual muda).
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiErrorMessage } from "../../lib/api";
import type { Category, ChannelSummary, EntryPatchResponse, ProductDetail } from "../../lib/types";
import { AiGenerateButton, AiGenerateTagsButton } from "../AiGenerateButton";
import { DynamicFieldsCard } from "../DynamicFieldsCard";
import { TechnicalitiesTab } from "../TechnicalitiesTab";
import { CareInstructionsTab } from "../CareInstructionsTab";
import { SizeGuideTab } from "../SizeGuideTab";
import { MultiCategoryPicker } from "../MultiCategoryPicker";
import { CommonPhotosCard, type PhotoManager } from "../PhotosTab";
import { SizeCardsProduct, type GridManager } from "../GridTab";
import { OverrideFieldCard } from "./OverrideFieldCard";
import { MlFichaCard } from "./MlFichaCard";
import { titleCounter } from "../../lib/ml-ficha";
import { Button } from "../ui/button";
import { Input, Textarea } from "../ui/input";
import { Label } from "../ui/label";
import { Select } from "../ui/select";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogTitle } from "../ui/dialog";
import { useAuth } from "../../contexts/AuthContext";
import { formatDate, toNumberOrNull } from "../../lib/utils";
import { toEditableText, fromEditableText } from "../../lib/richtext";

interface BaseProductPanelProps {
  product: ProductDetail;
  slug: string;
  categories: Category[];
  disabled: boolean;
  /** RBAC configuravel: capacidade photos.write, INDEPENDENTE de products.write
   * (BUG-01 do QA — perfil pode ter uma sem ter a outra). */
  photosDisabled: boolean;
  photoMgr: PhotoManager;
  gridMgr: GridManager;
}

/**
 * SITUACAO DO PRODUTO (plano 2026-10-27-status-arquivado): Em operacao
 * (entries.status = draft) ou Arquivado. Salva na hora, SEPARADO do "Salvar" da
 * Base (o corpo do entryMutation nao leva status, entao salvar a Base nunca
 * desarquiva sem querer). Arquivar tira o produto da loja em todos os canais,
 * por isso so quem tem products.publish muda e a escolha pede confirmacao com a
 * lista de canais onde ele esta no ar.
 */
function ProductStatusCard({ product, slug }: { product: ProductDetail; slug: string }): JSX.Element {
  const qc = useQueryClient();
  const { can } = useAuth();
  const canChange = can("products.publish");
  const sku = product.sku;
  const archived = product.entry.status === "archived";
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Em quais canais o produto esta no ar: so consultado com o dialogo aberto.
  // A ficha traz uma publicacao por vez, entao os outros canais vem da ficha de
  // cada um (mesma chave de cache da ProductPage). Falha de leitura de um canal
  // (sem acesso) nao trava o arquivamento: aquele canal so nao entra na lista.
  const channelsQ = useQuery<ChannelSummary[]>({
    queryKey: ["channels"],
    queryFn: async () => (await api.get<ChannelSummary[]>("/api/channels")).data,
    enabled: confirmOpen,
  });
  const otherChannels = (channelsQ.data ?? []).filter((c) => c.slug !== slug);
  const otherQs = useQueries({
    queries: otherChannels.map((c) => ({
      queryKey: ["product", c.slug, sku],
      queryFn: async () => (await api.get<ProductDetail>(`/api/channels/${c.slug}/products/${sku}`)).data,
      enabled: confirmOpen,
      retry: false,
      staleTime: 30_000,
    })),
  });
  const checking = confirmOpen && (channelsQ.isLoading || otherQs.some((q) => q.isLoading));
  const isOnChannel = (p: ProductDetail["publication"] | undefined): boolean =>
    Boolean(p && p.external_id !== null && p.status !== "unpublished");
  const currentName = (channelsQ.data ?? []).find((c) => c.slug === slug)?.name ?? slug;
  const liveIn: string[] = [];
  if (isOnChannel(product.publication)) liveIn.push(currentName);
  otherChannels.forEach((c, i) => {
    if (isOnChannel(otherQs[i]?.data?.publication)) liveIn.push(c.name);
  });

  const statusMutation = useMutation({
    mutationFn: async (status: "draft" | "archived") =>
      (await api.patch<EntryPatchResponse>(`/api/entries/${sku}`, { status })).data,
    onSuccess: (d, status) => {
      if (status === "archived") {
        const n = d.unpublish_jobs ?? 0;
        toast.success(
          n > 0
            ? `Produto arquivado. Saindo da loja em ${n} canal${n === 1 ? "" : "is"}.`
            : "Produto arquivado. Ele não estava no ar em nenhum canal.",
        );
      } else {
        toast.success("Produto desarquivado. Voltou como rascunho: publique de novo quando quiser.");
      }
      setConfirmOpen(false);
      for (const key of ["product", "channel-products", "channel-facets", "channels", "channel", "fila-de-trabalho", "search"]) {
        void qc.invalidateQueries({ queryKey: [key] });
      }
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const onSelect = (value: string): void => {
    if (value === "archived") setConfirmOpen(true);
    else statusMutation.mutate("draft");
  };

  const noPermission = "Seu perfil não pode arquivar nem desarquivar produtos.";
  const archivedBy = product.entry.archived_by_name;
  const archivedAt = product.entry.archived_at;
  const archivedLine = archivedAt
    ? `Arquivado${archivedBy ? ` por ${archivedBy}` : ""} em ${formatDate(archivedAt)}`
    : "Arquivado";

  return (
    <div
      className="stack"
      style={{
        gap: ".5rem",
        marginTop: ".75rem",
        padding: ".625rem .75rem",
        borderRadius: "var(--radius-md)",
        border: ".0625rem solid var(--color-line)",
        background: archived ? "var(--color-soft)" : undefined,
      }}
    >
      <div className="field">
        <Label htmlFor="product-status">Situação do produto</Label>
        <div className="cluster" style={{ alignItems: "center" }}>
          <Select
            id="product-status"
            value={archived ? "archived" : "draft"}
            onChange={(e) => onSelect(e.target.value)}
            disabled={!canChange || statusMutation.isPending}
            title={!canChange ? noPermission : undefined}
            style={{ maxWidth: "16rem" }}
          >
            <option value="draft">Em operação</option>
            <option value="archived">Arquivado</option>
          </Select>
          {archived && canChange ? (
            <Button
              type="button"
              variant="secondary"
              onClick={() => statusMutation.mutate("draft")}
              loading={statusMutation.isPending}
            >
              Desarquivar
            </Button>
          ) : null}
        </div>
        <span className="field__hint">
          {archived
            ? `${archivedLine}. Fora das listas, da fila e da loja em todos os canais; segue na busca.`
            : "Arquive o produto que está ativo no Sisplan mas nunca vai para a vitrine. Ele sai das filas e dos contadores."}
          {!canChange ? ` ${noPermission}` : ""}
        </span>
      </div>

      <Dialog open={confirmOpen} onOpenChange={(open) => (!statusMutation.isPending ? setConfirmOpen(open) : undefined)}>
        <DialogContent style={{ width: "min(26rem, 92vw)" }}>
          <div className="stack text-center sm:text-left" style={{ gap: ".25rem" }}>
            <DialogTitle>Arquivar este produto?</DialogTitle>
            <DialogDescription>
              SKU {sku}. Ele sai das listas, da fila "Para fazer hoje" e dos contadores de todos os canais, e não é mais
              publicado. Dá para desarquivar quando quiser.
            </DialogDescription>
          </div>
          <p style={{ margin: ".75rem 0 0", fontWeight: 700 }}>
            {checking
              ? "Conferindo em quais canais ele está no ar…"
              : liveIn.length > 0
                ? `Vai sair da loja em: ${liveIn.join(", ")} (e de qualquer outro canal em que esteja no ar).`
                : "Ele não está no ar em nenhum canal que você acessa; se estiver em outro, sai de lá também."}
          </p>
          <DialogFooter className="mt-4">
            <DialogClose asChild>
              <Button type="button" variant="secondary" disabled={statusMutation.isPending}>
                Cancelar
              </Button>
            </DialogClose>
            <Button
              type="button"
              variant="destructive"
              onClick={() => statusMutation.mutate("archived")}
              loading={statusMutation.isPending}
              disabled={checking}
            >
              Arquivar produto
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function flattenCategories(cats: Category[], prefix = ""): { id: number; label: string }[] {
  const out: { id: number; label: string }[] = [];
  for (const c of cats) {
    const label = prefix ? `${prefix} › ${c.name}` : c.name;
    out.push({ id: c.id, label });
    if (c.children && c.children.length > 0) out.push(...flattenCategories(c.children, label));
  }
  return out;
}

function isoToLocalInput(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}

export function BaseProductPanel({ product, slug, categories, disabled, photosDisabled, photoMgr, gridMgr }: BaseProductPanelProps): JSX.Element {
  const qc = useQueryClient();
  const flatCategories = useMemo(() => flattenCategories(categories), [categories]);
  const queryKey = ["product", slug, product.sku];

  // ---- base ----
  const [name, setName] = useState(product.entry.name_base ?? "");
  const [descShort, setDescShort] = useState(product.entry.description_short_base ?? "");
  const initialDescLong = useMemo(() => toEditableText(product.entry.description_long_base), [product]);
  const [descLong, setDescLong] = useState(initialDescLong.text);
  const [descLongIsRich, setDescLongIsRich] = useState(initialDescLong.isRichHtml);
  const [catIds, setCatIds] = useState<number[]>(product.entry.category_ids ?? []);
  const [isGift, setIsGift] = useState(product.entry.is_gift === true);
  const [composition, setComposition] = useState(product.entry.composition ?? "");
  // Canal Mercado Livre: a API manda ml_ficha. Nele a ficha esconde o que o ML nao usa (descricao curta, SEO, tags,
  // promocao) e troca "Campos do canal" pela ficha tecnica com valor herdado e origem.
  const mlFicha = product.ml_ficha ?? null;
  const isMl = mlFicha !== null;

  // ---- override (canal atual) ----
  const [nameOv, setNameOv] = useState(product.publication.name_override ?? "");
  const [descShortOv, setDescShortOv] = useState(product.publication.description_short_override ?? "");
  const initialDescLongOv = useMemo(() => toEditableText(product.publication.description_long_override), [product]);
  const [descLongOv, setDescLongOv] = useState(initialDescLongOv.text);
  const [descLongOvIsRich, setDescLongOvIsRich] = useState(initialDescLongOv.isRichHtml);
  const [catOv, setCatOv] = useState<number | "">(product.publication.category_id_override ?? "");

  // ---- switches "Sobrescrever" (derivados do publication atual ao carregar/trocar produto) ----
  const [nameSw, setNameSw] = useState(product.publication.name_override !== null);
  const [descShortSw, setDescShortSw] = useState(product.publication.description_short_override !== null);
  const [descLongSw, setDescLongSw] = useState(product.publication.description_long_override !== null);
  const [catSw, setCatSw] = useState(product.publication.category_id_override !== null);

  useEffect(() => {
    setName(product.entry.name_base ?? "");
    setDescShort(product.entry.description_short_base ?? "");
    const dl = toEditableText(product.entry.description_long_base);
    setDescLong(dl.text);
    setDescLongIsRich(dl.isRichHtml);
    setCatIds(product.entry.category_ids ?? []);
    setIsGift(product.entry.is_gift === true);
    setComposition(product.entry.composition ?? "");
    setNameOv(product.publication.name_override ?? "");
    setDescShortOv(product.publication.description_short_override ?? "");
    const dlOv = toEditableText(product.publication.description_long_override);
    setDescLongOv(dlOv.text);
    setDescLongOvIsRich(dlOv.isRichHtml);
    setCatOv(product.publication.category_id_override ?? "");
    setNameSw(product.publication.name_override !== null);
    setDescShortSw(product.publication.description_short_override !== null);
    setDescLongSw(product.publication.description_long_override !== null);
    setCatSw(product.publication.category_id_override !== null);
  }, [product]);

  const overrideCount = [nameSw, descShortSw, descLongSw, catSw].filter(Boolean).length;

  const entryMutation = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {
        name_base: name || null,
        description_short_base: descShort || null,
        description_long_base: descLong ? fromEditableText(descLong, descLongIsRich) : null,
        category_ids: catIds,
        is_gift: isGift,
        composition: composition.trim() || null,
      };
      await api.patch(`/api/entries/${product.sku}`, body);
    },
    onSuccess: () => {
      toast.success("Dados base salvos");
      void qc.invalidateQueries({ queryKey });
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const publicationMutation = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {};
      body.name_override = nameSw ? nameOv || null : null;
      body.description_short_override = descShortSw ? descShortOv || null : null;
      body.description_long_override = descLongSw ? (descLongOv ? fromEditableText(descLongOv, descLongOvIsRich) : null) : null;
      body.category_id_override = catSw && catOv !== "" ? Number(catOv) : null;
      await api.patch(`/api/channels/${slug}/products/${product.sku}`, body);
    },
    onSuccess: () => {
      toast.success(`Override do canal "${slug}" salvo`);
      void qc.invalidateQueries({ queryKey });
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  // ---- SEO ----
  const [seoTitle, setSeoTitle] = useState(product.publication.seo_title ?? "");
  const [seoMeta, setSeoMeta] = useState(product.publication.seo_meta_description ?? "");
  const [seoKeyword, setSeoKeyword] = useState(product.publication.seo_focus_keyword ?? "");
  const [seoSlug, setSeoSlug] = useState(product.publication.seo_slug ?? "");
  const [suggestedTags, setSuggestedTags] = useState<string[] | null>(null);
  useEffect(() => {
    setSeoTitle(product.publication.seo_title ?? "");
    setSeoMeta(product.publication.seo_meta_description ?? "");
    setSeoKeyword(product.publication.seo_focus_keyword ?? "");
    setSeoSlug(product.publication.seo_slug ?? "");
  }, [product]);

  const seoMutation = useMutation({
    mutationFn: async () => {
      await api.patch(`/api/channels/${slug}/products/${product.sku}`, {
        seo_title: seoTitle || null,
        seo_meta_description: seoMeta || null,
        seo_focus_keyword: seoKeyword || null,
        seo_slug: seoSlug || null,
      });
    },
    onSuccess: () => {
      toast.success("SEO salvo");
      void qc.invalidateQueries({ queryKey });
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const previewTitle = seoTitle || product.entry.name_base || product.sisplan.name || product.sku;
  const previewUrl = `atacado.hiitfitwear.com/produto/${seoSlug || slugify(previewTitle)}`;
  const previewMeta = seoMeta || product.entry.description_short_base || "";

  // ---- Promocao (ADR-011 F2) ----
  const pub = product.publication;
  const [salePrice, setSalePrice] = useState(pub.sale_price ?? "");
  const [startsAt, setStartsAt] = useState(isoToLocalInput(pub.sale_starts_at));
  const [endsAt, setEndsAt] = useState(isoToLocalInput(pub.sale_ends_at));
  useEffect(() => {
    setSalePrice(pub.sale_price ?? "");
    setStartsAt(isoToLocalInput(pub.sale_starts_at));
    setEndsAt(isoToLocalInput(pub.sale_ends_at));
  }, [pub.sale_price, pub.sale_starts_at, pub.sale_ends_at]);

  const base = toNumberOrNull(product.sisplan.price);
  const hasPromo = pub.sale_price != null;

  const savePromoMutation = useMutation({
    mutationFn: async () => {
      await api.put(`/api/channels/${slug}/products/${product.sku}/promo`, {
        sale_price: Number(salePrice),
        sale_starts_at: startsAt ? new Date(startsAt).toISOString() : null,
        sale_ends_at: endsAt ? new Date(endsAt).toISOString() : null,
      });
    },
    onSuccess: () => {
      toast.success("Promoção salva. Vai ao ar na próxima republicação (manual ou automática, até 24h).");
      void qc.invalidateQueries({ queryKey });
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const clearPromoMutation = useMutation({
    mutationFn: async () => {
      await api.delete(`/api/channels/${slug}/products/${product.sku}/promo`);
    },
    onSuccess: () => {
      toast.success("Promoção removida. A loja volta ao preço normal na próxima republicação.");
      void qc.invalidateQueries({ queryKey });
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const priceNum = Number(salePrice);
  const priceInvalid = salePrice !== "" && (!Number.isFinite(priceNum) || priceNum <= 0);
  const aboveBase = base != null && Number.isFinite(priceNum) && priceNum >= base;
  const windowInvalid = Boolean(startsAt && endsAt && endsAt <= startsAt);
  const canSavePromo = !disabled && salePrice !== "" && !priceInvalid && !aboveBase && !windowInvalid;

  return (
    <div className="stack" style={{ gap: ".75rem" }}>
      {/* Sisplan (read-only) — a linha compacta com preco/estoque/ultima sync
          subiu pra ProductPage.tsx (abaixo do cabecalho, vale pra Base E
          Cor). Aqui so ficam os campos editaveis (Check 0: mesma mutation
          de antes, so a moldura mudou). */}
      <div className="card" style={{ borderTop: ".1875rem solid var(--color-lilac)" }}>
        <div className="cluster" style={{ alignItems: "baseline" }}>
          <div style={{ fontSize: "var(--text-xl)", fontWeight: 900 }}>Base do produto</div>
          <span className="card__meta">vale para todas as cores e todos os canais</span>
        </div>

        <ProductStatusCard product={product} slug={slug} />

        <div className="cluster" style={{ background: "var(--color-soft)", borderRadius: "var(--radius-md)", padding: ".625rem .75rem", marginTop: ".75rem" }}>
          <span className="field__hint grow">
            Cada campo mostra o valor <b style={{ color: "var(--color-ink)" }}>base</b>, compartilhado por
            todos os canais. Ligue "Sobrescrever" para criar um valor só do canal{" "}
            <b style={{ color: "var(--color-ink)" }}>{slug}</b> — os outros campos continuam herdando.
          </span>
          <span style={{ fontSize: "var(--text-sm)", fontWeight: 800, color: "var(--color-ink-2)", whiteSpace: "nowrap" }}>
            {overrideCount} com override
          </span>
        </div>

        <div className="stack" style={{ gap: ".625rem", marginTop: ".625rem" }}>
          <OverrideFieldCard
            id="name"
            label="Nome"
            type="text"
            baseValue={name}
            overrideActive={nameSw}
            overrideValue={nameOv}
            onToggleOverride={setNameSw}
            onChangeBase={setName}
            onChangeOverride={setNameOv}
            channelSlug={slug}
            disabled={disabled}
            actions={
              isMl ? (
                <AiGenerateButton
                  slug={slug}
                  sku={product.sku}
                  target="title"
                  label="Sugerir título"
                  onResult={(t) => {
                    setNameOv(t);
                    setNameSw(true);
                  }}
                />
              ) : (
                <AiGenerateButton slug={slug} sku={product.sku} target="name" label="Sugerir nome" onResult={nameSw ? setNameOv : setName} />
              )
            }
            hint={
              mlFicha ? (
                <p className="field__hint" style={{ marginTop: ".375rem" }}>
                  Título no Mercado Livre (salvo): <b style={{ color: "var(--color-ink)" }}>{mlFicha.title.value || "—"}</b>{" "}
                  <span className="chip" style={titleCounter(mlFicha.title.length, mlFicha.title.max).over ? { background: "var(--color-bad)", color: "white" } : undefined}>
                    {titleCounter(mlFicha.title.length, mlFicha.title.max).text}
                  </span>
                  {" "}Sem "Sobrescrever", o título nasce deste nome; com ele, vale o que você escrever.
                </p>
              ) : undefined
            }
          />
          {!isMl ? (
          <OverrideFieldCard
            id="desc-short"
            label="Descrição curta"
            type="textarea"
            rows={2}
            baseValue={descShort}
            overrideActive={descShortSw}
            overrideValue={descShortOv}
            onToggleOverride={setDescShortSw}
            onChangeBase={setDescShort}
            onChangeOverride={setDescShortOv}
            channelSlug={slug}
            disabled={disabled}
            actions={
              <AiGenerateButton
                slug={slug}
                sku={product.sku}
                target="description_short"
                onResult={descShortSw ? setDescShortOv : setDescShort}
              />
            }
          />
          ) : null}
          <OverrideFieldCard
            id="desc-long"
            label="Descrição longa"
            type="textarea"
            rows={7}
            baseValue={descLong}
            overrideActive={descLongSw}
            overrideValue={descLongOv}
            onToggleOverride={setDescLongSw}
            onChangeBase={(v) => {
              setDescLong(v);
              setDescLongIsRich(false);
            }}
            onChangeOverride={(v) => {
              setDescLongOv(v);
              setDescLongOvIsRich(false);
            }}
            channelSlug={slug}
            disabled={disabled}
            actions={
              <AiGenerateButton
                slug={slug}
                sku={product.sku}
                target="description_long"
                onResult={descLongSw ? (t) => { setDescLongOv(t); setDescLongOvIsRich(false); } : (t) => { setDescLong(t); setDescLongIsRich(false); }}
              />
            }
            hint={
              (descLongSw ? descLongOvIsRich : descLongIsRich) ? (
                <p className="field__hint" style={{ color: "var(--color-warn)", marginTop: ".375rem" }}>
                  Este texto tem formatação avançada que o editor simples não reconhece (negrito,
                  listas, etc). Está sendo mostrado como código HTML para não perder nada.
                </p>
              ) : (
                <p className="field__hint" style={{ marginTop: ".375rem" }}>
                  Digite parágrafos separados por uma linha em branco (Enter duas vezes).
                  {isMl ? " No Mercado Livre a descrição vai como texto simples: sem negrito, link, telefone nem emoji. A lista de tecnologias e os cuidados são acrescentados sozinhos." : ""}
                </p>
              )
            }
          />


          <div className="override">
            <Label htmlFor="composition">Composição</Label>
            <Input
              id="composition"
              value={composition}
              onChange={(e) => setComposition(e.target.value)}
              disabled={disabled}
              maxLength={500}
              placeholder="94% viscose, 6% elastano"
            />
            <p className="field__hint" style={{ marginTop: ".375rem" }}>
              Dado de etiqueta, vale para todos os canais. No Mercado Livre a fibra de maior percentual vira o material principal.
              {isMl ? " Para mudar só neste canal, use a ficha técnica abaixo." : " No atacado, o campo \"Material (composição)\" em Campos do canal continua valendo."}
            </p>
          </div>

          <div className="override">
            <label htmlFor="is-gift" className="cluster" style={{ gap: ".5rem", alignItems: "flex-start", flexWrap: "nowrap" }}>
              <input
                id="is-gift"
                type="checkbox"
                checked={isGift}
                onChange={(e) => setIsGift(e.target.checked)}
                disabled={disabled}
                style={{ marginTop: ".25rem", flexShrink: 0 }}
              />
              <span>
                <span style={{ fontWeight: 800 }}>Brinde (nunca vai ao Mercado Livre)</span>
                <span className="field__hint" style={{ display: "block" }}>
                  Marque para itens dados de presente. Mesmo com estoque e foto, a publicação automática ignora este SKU. Vale ao salvar a base.
                </span>
              </span>
            </label>
          </div>
          {/* Categorias — override e um UNICO id (schema atual, sem migration nesta onda).
              "sem categoria neste canal" NAO e oferecido: category_id_override e number|null,
              nao ha como distinguir "sem override" de "override vazio" (fix-not-banner: ou
              funciona, ou nao existe — nao fica um controle que mente). */}
          <div className={cn2(catSw)}>
            <div className="cluster" style={{ marginBottom: ".5rem" }}>
              <Label>Categorias</Label>
              <span className="chip" style={catSw ? { background: "var(--color-coral-soft)", color: "var(--color-coral)" } : undefined}>
                {catSw ? `override ${slug}` : "base"}
              </span>
              {!disabled ? (
                <>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={catSw}
                    title={`Sobrescrever categoria no canal ${slug}`}
                    onClick={() => setCatSw((v) => !v)}
                    className="switch"
                    style={{ marginLeft: "auto", background: catSw ? "var(--color-coral)" : "var(--color-line)" }}
                  />
                  <span style={{ fontSize: "var(--text-sm)", fontWeight: 700, color: "var(--color-ink-2)" }}>Sobrescrever</span>
                </>
              ) : null}
            </div>
            {catSw ? (
              <>
                <select
                  className="input"
                  value={catOv === "" ? "" : String(catOv)}
                  onChange={(e) => setCatOv(e.target.value === "" ? "" : Number(e.target.value))}
                  disabled={disabled}
                >
                  <option value="">Escolher categoria…</option>
                  {flatCategories.map((c) => (
                    <option key={c.id} value={String(c.id)}>{c.label}</option>
                  ))}
                </select>
                <p className="field__hint" style={{ marginTop: ".375rem" }}>
                  O override troca TODAS as categorias base por esta UMA categoria neste canal — o
                  sistema não permite "sem categoria neste canal" (o campo só guarda um id).
                </p>
                <div className="cluster" style={{ marginTop: ".5rem" }}>
                  <span className="clamp-2 grow" style={{ background: "var(--color-soft)", borderRadius: "var(--radius-sm)", padding: ".25rem .5rem", fontSize: "var(--text-sm)" }}>
                    Base: {flatCategories.filter((c) => catIds.includes(c.id)).map((c) => c.label).join(", ") || "—"}
                  </span>
                  {!disabled ? (
                    <button type="button" onClick={() => setCatSw(false)} style={{ border: 0, background: "transparent", color: "var(--color-coral)", fontWeight: 800, cursor: "pointer" }}>
                      Voltar ao valor base
                    </button>
                  ) : null}
                </div>
              </>
            ) : (
              <MultiCategoryPicker categories={flatCategories} selected={catIds} onChange={setCatIds} disabled={disabled} />
            )}
          </div>
        </div>

        {!disabled ? (
          <div className="cluster" style={{ justifyContent: "flex-end", marginTop: ".75rem" }}>
            <Button variant="secondary" onClick={() => publicationMutation.mutate()} loading={publicationMutation.isPending}>
              Salvar override do canal
            </Button>
            <Button onClick={() => entryMutation.mutate()} loading={entryMutation.isPending}>
              Salvar base
            </Button>
          </div>
        ) : null}
      </div>

      <CommonPhotosCard product={product} slug={slug} disabled={photosDisabled} mgr={photoMgr} />

      <SizeCardsProduct mgr={gridMgr} disabled={disabled} />

      <div className="card">
        <div style={{ fontSize: "var(--text-xl)", fontWeight: 900, marginBottom: ".5rem" }}>Tecnicidades</div>
        <TechnicalitiesTab product={product} slug={slug} disabled={disabled} />
      </div>

      <div className="card">
        <div style={{ fontSize: "var(--text-xl)", fontWeight: 900, marginBottom: ".5rem" }}>Instruções de lavagem</div>
        <CareInstructionsTab product={product} slug={slug} disabled={disabled} />
      </div>

      <div className="card">
        <div style={{ fontSize: "var(--text-xl)", fontWeight: 900, marginBottom: ".5rem" }}>Guia de tamanhos</div>
        <SizeGuideTab product={product} slug={slug} disabled={disabled} />
      </div>

      {mlFicha ? (
        <MlFichaCard
          slug={slug}
          sku={product.sku}
          ficha={mlFicha}
          fieldOverrides={product.publication.field_overrides ?? {}}
          priceText={product.sisplan?.price != null ? String(product.sisplan.price) : null}
          disabled={disabled}
        />
      ) : (
        <DynamicFieldsCard
          slug={slug}
          sku={product.sku}
          initialOverrides={product.publication.field_overrides ?? {}}
          disabled={disabled}
        />
      )}

      {!isMl ? (
      <div className="card">
        <div className="cluster" style={{ alignItems: "baseline" }}>
          <div style={{ fontSize: "var(--text-xl)", fontWeight: 900 }}>SEO e preço</div>
          <span className="card__meta">
            por canal · preço normal {base != null ? `R$ ${base.toFixed(2)}` : "—"} (Sisplan)
          </span>
        </div>

        <div className="stack" style={{ gap: ".625rem", marginTop: ".75rem" }}>
          <div>
            <div className="cluster" style={{ justifyContent: "space-between" }}>
              <Label htmlFor="seo-title">SEO title</Label>
              <AiGenerateButton slug={slug} sku={product.sku} target="seo_title" onResult={setSeoTitle} />
            </div>
            <Input id="seo-title" value={seoTitle} onChange={(e) => setSeoTitle(e.target.value)} placeholder="Título otimizado pra SEO (50-60 chars ideal)" disabled={disabled} />
          </div>
          <div>
            <div className="cluster" style={{ justifyContent: "space-between" }}>
              <Label htmlFor="seo-meta">Meta description</Label>
              <AiGenerateButton slug={slug} sku={product.sku} target="seo_meta_description" onResult={setSeoMeta} />
            </div>
            <Textarea id="seo-meta" value={seoMeta} onChange={(e) => setSeoMeta(e.target.value)} rows={3} disabled={disabled} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <div className="cluster" style={{ justifyContent: "space-between" }}>
                <Label htmlFor="seo-keyword">Focus keyword (RankMath)</Label>
                <AiGenerateButton slug={slug} sku={product.sku} target="seo_focus_keyword" onResult={setSeoKeyword} />
              </div>
              <Input id="seo-keyword" value={seoKeyword} onChange={(e) => setSeoKeyword(e.target.value)} disabled={disabled} />
            </div>
            <div>
              <Label htmlFor="seo-slug">URL slug</Label>
              <Input id="seo-slug" value={seoSlug} onChange={(e) => setSeoSlug(e.target.value)} placeholder={slugify(previewTitle)} disabled={disabled} />
            </div>
          </div>

          <div style={{ background: "var(--color-soft)", borderRadius: "var(--radius-md)", padding: ".875rem" }}>
            <div className="filters__group-label">Preview Google</div>
            <div style={{ fontWeight: 800, color: "var(--color-coral)", marginTop: ".25rem" }} className="truncate">{previewTitle}</div>
            <div style={{ fontSize: "var(--text-sm)", color: "var(--color-good)", fontWeight: 700, wordBreak: "break-all" }}>{previewUrl}</div>
            <div className="clamp-2" style={{ fontSize: "var(--text-base)", color: "var(--color-ink-2)", marginTop: ".25rem" }}>{previewMeta || "(sem meta description)"}</div>
          </div>

          <div>
            <div className="cluster" style={{ justifyContent: "space-between", marginBottom: ".5rem" }}>
              <Label>Tags sugeridas</Label>
              <AiGenerateTagsButton slug={slug} sku={product.sku} onResult={setSuggestedTags} />
            </div>
            {suggestedTags === null ? (
              <p className="field__hint">
                Clique em "Sugerir tags com IA" pra gerar sugestões. A tag não é criada automaticamente:
                clique numa sugestão pra copiar e cadastre em <span className="mono">/admin/tags</span> se
                ainda não existir.
              </p>
            ) : suggestedTags.length === 0 ? (
              <p className="field__hint">Nenhuma sugestão pra este produto.</p>
            ) : (
              <div className="cluster">
                {suggestedTags.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    className="chip"
                    style={{ cursor: "pointer" }}
                    onClick={() => navigator.clipboard.writeText(tag).then(() => toast.success(`"${tag}" copiada`), () => toast.error("Falha ao copiar"))}
                    title="Clique pra copiar"
                  >
                    {tag}
                  </button>
                ))}
              </div>
            )}
          </div>

          {!disabled ? (
            <div className="cluster" style={{ justifyContent: "flex-end" }}>
              <Button onClick={() => seoMutation.mutate()} loading={seoMutation.isPending}>
                Salvar SEO
              </Button>
            </div>
          ) : null}

          <div style={{ borderTop: ".0625rem solid var(--color-line)", paddingTop: ".625rem" }} className="stack">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <label style={{ fontSize: "var(--text-base)", fontWeight: 800 }}>
                <span className="field__hint" style={{ display: "block", marginBottom: ".25rem" }}>Preço promocional (R$)</span>
                <Input type="number" min="0.01" step="0.01" value={salePrice} onChange={(e) => setSalePrice(e.target.value)} disabled={disabled} aria-invalid={priceInvalid || aboveBase} />
              </label>
              <label style={{ fontSize: "var(--text-base)", fontWeight: 800 }}>
                <span className="field__hint" style={{ display: "block", marginBottom: ".25rem" }}>Início (opcional)</span>
                <Input type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} disabled={disabled} />
              </label>
              <label style={{ fontSize: "var(--text-base)", fontWeight: 800 }}>
                <span className="field__hint" style={{ display: "block", marginBottom: ".25rem" }}>Fim (opcional)</span>
                <Input type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} disabled={disabled} aria-invalid={windowInvalid} />
              </label>
            </div>
            {aboveBase ? <p style={{ color: "var(--color-bad)", fontSize: "var(--text-base)" }}>O preço promocional precisa ser MENOR que o preço normal.</p> : null}
            {windowInvalid ? <p style={{ color: "var(--color-bad)", fontSize: "var(--text-base)" }}>O fim da promoção precisa ser depois do início.</p> : null}
            {!disabled ? (
              <div className="cluster">
                <Button onClick={() => savePromoMutation.mutate()} disabled={!canSavePromo || savePromoMutation.isPending}>
                  {savePromoMutation.isPending ? "Salvando..." : "Salvar promoção"}
                </Button>
                {hasPromo ? (
                  <Button variant="secondary" onClick={() => clearPromoMutation.mutate()} disabled={clearPromoMutation.isPending}>
                    {clearPromoMutation.isPending ? "Removendo..." : "Limpar promoção"}
                  </Button>
                ) : null}
              </div>
            ) : null}
            <p className="field__hint">
              Vale para todas as cores e tamanhos deste produto neste canal. Sem datas, a promoção vale
              até ser removida. Alterações aparecem na próxima republicação (manual ou automática, até 24h).
            </p>
          </div>
        </div>
      </div>
      ) : null}
    </div>
  );
}

function cn2(active: boolean): string {
  return active ? "override override--on" : "override";
}
